import { isAxiosError } from 'axios';
import pRetry from 'p-retry';

import { SESSION_CAP_REACHED, SESSION_REVOKED } from '@a1/shared';
import { SESSION_ENDED_MESSAGE, SESSION_ENDED_STORAGE_KEY, SESSION_REVOKED_MESSAGE } from 'constants-ts/session';
import { MissingAccessTokenError, MissingRefreshTokenError, TokenStorageUnavailableError } from 'services/auth/authErrors';
import { getAccessToken, isAccessTokenExpiringSoon, isStorageUnavailable, retryHydrate } from 'services/auth/authTransport';
import { store } from 'store/index';
import callAzureFunctionAuth from 'utils-ts/call-azure-function-auth';
import { getUserTypeLogoutPath, handleLogout, shouldSkipLogoutRedirectForPath } from 'utils/logout';

const ACCESS_TOKEN_REFRESH_THRESHOLD_MINUTES = 5;
const REFRESH_REQUEST_TIMEOUT_MS = 10000;
const REFRESH_RETRY_MAX_ELAPSED_MS = 15000;

const isAuthFailure = (error: unknown): boolean =>
  error instanceof MissingAccessTokenError ||
  error instanceof MissingRefreshTokenError ||
  (isAxiosError(error) && error.response?.status === 401);

const isFatal = (error: unknown): boolean => isAuthFailure(error) || error instanceof TokenStorageUnavailableError;

const failureReason = (error: unknown): string | undefined =>
  isAxiosError(error) ? (error.response?.data as { data?: { reason?: string } } | undefined)?.data?.reason : undefined;

const isSessionRevoked = (error: unknown): boolean => failureReason(error) === SESSION_REVOKED;

const SESSION_ENDED_MESSAGES: Record<string, string> = {
  [SESSION_REVOKED]: SESSION_REVOKED_MESSAGE,
  [SESSION_CAP_REACHED]: SESSION_ENDED_MESSAGE
};

const requestRefresh = () =>
  callAzureFunctionAuth({
    url: '/auth-refresh-access',
    method: 'get',
    usesRefreshToken: true,
    timeout: REFRESH_REQUEST_TIMEOUT_MS
  });

const retryRefresh = () =>
  pRetry(requestRefresh, {
    shouldRetry: ({ error }) => !isFatal(error),
    retries: 3,
    randomize: true,
    maxRetryTime: REFRESH_RETRY_MAX_ELAPSED_MS
  });

let inFlightRefresh: Promise<unknown> | null = null;

const refreshOnce = () => {
  inFlightRefresh ??= retryRefresh().finally(() => {
    inFlightRefresh = null;
  });

  return inFlightRefresh;
};

let endingSession = false;

const endSession = async () => {
  if (endingSession) return;
  endingSession = true;

  const userData = store.getState().user?.data as { type?: string } | undefined;
  const logoutPath = getUserTypeLogoutPath(userData?.type);

  await handleLogout();

  if (!shouldSkipLogoutRedirectForPath(window.location.pathname)) {
    window.location.assign(logoutPath);
  }
};

const endSessionWith = async (message: string | undefined) => {
  if (message) {
    localStorage.setItem(SESSION_ENDED_STORAGE_KEY, message);
  }

  await endSession();
};

export const recoverSession = async (): Promise<void> => {
  if (!isStorageUnavailable()) return;

  await retryHydrate();
};

/**
 * Ends the session when a protected request was rejected because it was revoked server-side.
 * @param {unknown} error A rejection from any authenticated request.
 */
export const handleRevokedSession = async (error: unknown): Promise<void> => {
  if (!isSessionRevoked(error)) return;

  await endSessionWith(SESSION_REVOKED_MESSAGE);
};

/**
 * Refreshes the access token when it is within `thresholdMinutes` of expiry, and throws if it cannot.
 * @param {number} thresholdMinutes
 */
export const ensureFreshAccessToken = async (thresholdMinutes = ACCESS_TOKEN_REFRESH_THRESHOLD_MINUTES) => {
  if (!getAccessToken()) {
    throw isStorageUnavailable() ? new TokenStorageUnavailableError() : new MissingAccessTokenError();
  }

  if (!isAccessTokenExpiringSoon(thresholdMinutes)) {
    return;
  }

  await refreshOnce();
};

/**
 * Refreshes the access token if it is expiring, signing the user out only on a genuine auth failure.
 * @param {number} [thresholdMinutes]
 * @returns {Promise<boolean>} whether the token is usable.
 */
export const refreshAccessToken = async (thresholdMinutes?: number): Promise<boolean> => {
  try {
    await ensureFreshAccessToken(thresholdMinutes);
    return true;
  } catch (error) {
    if (isAuthFailure(error)) {
      await endSessionWith(SESSION_ENDED_MESSAGES[failureReason(error) ?? '']);
      return false;
    }

    console.error('Token refresh failed, keeping the session for the next attempt:', error);
    return false;
  }
};
